/**
 * useEquityBenchmark — singleton-cached fetch of the Damodaran
 * long-run US equity / T-Bill / T-Bond real returns from the backend
 * cache (populated by the yearly `damodaran-benchmark` warmer).
 *
 * Written as a module-level singleton so every consumer across the
 * page (Portfolio Contrast Card, MetricsRow, Valuation Matrix, etc.)
 * shares one in-flight promise + one final value — no dogpile GET
 * requests. Falls back to sane last-known-good defaults on any
 * network failure so downstream UI always renders.
 *
 * Jul 7, 2026.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const DEFAULTS = {
  historical_real_equity_pct: 6.7,
  historical_real_tbill_pct: 0.3,
  historical_real_tbond_pct: 1.5,
  current_real_tbond_pct: null,
  period: '1928\u20132023',
  is_default: true,
};

let cached = null;   // final value once resolved
let inflight = null; // shared promise while fetching

function fetchOnce() {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = axios
    .get(`${process.env.REACT_APP_BACKEND_URL}/api/equity-benchmark/damodaran`, { timeout: 5000 })
    .then((r) => {
      cached = {
        historical_real_equity_pct: Number(r?.data?.historical_real_equity_pct) || DEFAULTS.historical_real_equity_pct,
        historical_real_tbill_pct: r?.data?.historical_real_tbill_pct ?? null,
        historical_real_tbond_pct: r?.data?.historical_real_tbond_pct ?? null,
        current_real_tbond_pct: r?.data?.current_real_tbond_pct ?? null,
        period: r?.data?.period || DEFAULTS.period,
        is_default: !!r?.data?.is_default,
      };
      return cached;
    })
    .catch(() => {
      cached = { ...DEFAULTS };
      return cached;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export default function useEquityBenchmark() {
  const [value, setValue] = useState(cached || DEFAULTS);
  useEffect(() => {
    let cancelled = false;
    fetchOnce().then((v) => { if (!cancelled) setValue(v); });
    return () => { cancelled = true; };
  }, []);
  return value;
}
